import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../../../components/ui/dialog';
import {
    CheckCircle,
    AlertCircle,
    XCircle,
    Cog,
    Server,
    Clock,
    MapPin,
    Wrench,
    Hash,
    RefreshCw,
    Download
} from 'lucide-react';
import { Device, DeviceStatus } from '../../../../device/types/device';
import { formatLocationString, getDeviceErrors } from '../../../../../utils/deviceUtils';
import { formatDate, formatResponseTime } from '../../../../../utils/unitUtils';
import { WeldTypeNames } from '../../../../device/types/device';
import { useDownloadProgress } from '../../../../../contexts/DownloadProgressContext';
import { weldParameterDownloadService } from '../../../../../services/weldParameterDownloadService';
import { WeldParameterDownloadDialog } from '../../../../../components/WeldParameterDownloadDialog';
import { DownloadProgressModal } from '../../../../../components/DownloadProgressModal';
import { weldParams } from '../../../../../redux/device/actions';
interface DeviceRowProps {
    device: Device;
    onManage: (device: Device) => void;
    onStatusUpdate: (device: Device) => void;
    stationLabel?: string;
    unassigned?: boolean;
    /** Render without the outer Card wrapper (used inside StationCard). */
    embedded?: boolean;
    /** Orientation label shown when embedded (Clockwise / Counterclockwise). */
    orientation?: string;
    onOpenWeldParameters?: () => void;
}

// Status badge colors
const statusBadgeColors: Record<any, string> = {
    Pending: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    Failed: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    CertificateAvailable: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    DownloadComplete: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    Deregistered: 'bg-gray-50 text-gray-700 border-gray-200',
    AdminApprovalPending: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    AdminApproved: "text-galvanizing-green bg-[rgba(31,154,58,0.08)]",
    AdminRejected: 'bg-gray-50 text-gray-700 border-gray-200',
    Register: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    PublishReady: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    AdminApproval: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
};




// User status map
const userStatusMap: Record<
    string,
    { label: string; color: string; backends: string[]; icon: any; description: string }
> = {
    'Approved': {
        label: "Registered",
        color: "text-emerald-400",
        backends: ["AdminApproved", "DownloadComplete"],
        icon: Server,
        description: "Device has been registered and ready to assign to project.",
    },
    'DeviceActive': {
        label: "Active",
        color: "text-green-700",
        backends: ["PublishReady"],
        icon: CheckCircle,
        description: "Devices have been assigned to project and are fully active.",
    },
    'Pending': {
        label: "Registration Pending",
        color: "text-amber-600",
        backends: ["Pending", "AdminApprovalPending", 'Register', "CertificateAvailable", 'Failed'],
        icon: Clock,
        description: "Devices are awaiting registration.",
    },
    'AdminApproval': {
        label: "Admin Approval",
        color: "text-blue-600",
        backends: ["AdminApproval"],
        icon: RefreshCw,
        description: "Devices are awaiting admin approval.",
    },
    'Decommissioned': {
        label: "Decommissioned",
        color: "text-red-600",
        backends: ["Deregistered", "AdminRejected"],
        icon: XCircle,
        description: "Devices are de-activated",
    },
};

const statusName: Record<any, string> = {
    Pending: 'Registration Pending',
    Failed: 'Registration Failed',
    CertificateAvailable: 'Registration Pending',
    DownloadComplete: 'Registered',
    Deregistered: 'Decommissioned',
    AdminApprovalPending: 'Awaiting Admin Approval',
    AdminApproved: "Approved",
    AdminRejected: 'Decommissioned',
    Register: 'Enable Re-registration',
    PublishReady: 'Active',
    AdminApproval: 'Admin Approval',
};

const getStatusIcon = (connectionStatus: string, hasErrors: boolean = false, isOnline: boolean = true) => {
    // If there are errors, always show error icon
    if (hasErrors) {
        return  !isOnline ? <XCircle className="h-5 w-5 text-white bg-red-500 rounded-full" /> : 
        <AlertCircle className="h-5 w-5 text-white bg-yellow-500 rounded-full" />;
    }

    // Show icon based on connection status
    switch (connectionStatus) {
        case 'Online':
            return <CheckCircle className="h-5 w-5 text-white bg-green-500 rounded-full" />;
        case 'Offline':
        case 'Error':
            return <XCircle className="h-5 w-5 text-white bg-red-500 rounded-full" />;
        case 'In Progress':
            return <AlertCircle className="h-5 w-5 text-white bg-yellow-500 rounded-full" />;
        case '':
        case null:
        case undefined:
            return <AlertCircle className="h-5 w-5 text-white bg-gray-500 rounded-full" />;
        default:
            return <AlertCircle className="h-5 w-5 text-white bg-orange-500 rounded-full" />;
    }
};

const getStatusBadge = (status: DeviceStatus) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${statusBadgeColors[status]}`}>
        {statusName[status] || status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
);

// Get device alerts from actual device data only
const getDeviceAlerts = (device: Device) => {
    const alerts = [];

    // Get errors from device operations
    const deviceErrors = getDeviceErrors(device);
    deviceErrors.forEach(error => {
        alerts.push({
            type: 'error',
            code: error.displayName,
            message: error.error
        });
    });

    // Check for connection status errors
    const connectionStatus = device.connection_status || device.deviceData?.connection_status;

    if (connectionStatus === 'Error' || connectionStatus === 'Offline') {
        alerts.push({
            type: 'error',
            code: 'Connection',
            message: `Device connection error: ${connectionStatus}`
        });
    }

    // Check for error data in device fields
    if (device.get_time?.error) {
        alerts.push({
            type: 'error',
            code: 'Time Sync',
            message: device.get_time.error
        });
    }

    if (device.deviceData?.get_time?.error) {
        alerts.push({
            type: 'error',
            code: 'Time Sync',
            message: device.deviceData.get_time.error
        });
    }

    // Check for empty or error location data
    if (!device.location?.data || device.location.data === '' || device.location.data === 'Error') {
        alerts.push({
            type: 'warning',
            code: 'Location',
            message: 'Device location not assigned'
        });
    }

    // Add status-based alerts
    if (device.status === 'Pending') {
        alerts.push({
            type: 'warning',
            code: '45',
            message: 'The Trial License expires soon.'
        });
    }

    if (device.status === 'Failed') {
        alerts.push({
            type: 'error',
            code: '18228',
            message: 'Device registration failed.'
        });
    }

    return alerts;
};

export const DeviceRow: React.FC<DeviceRowProps> = ({
    device,
    onManage,
    onStatusUpdate,
    stationLabel,
    unassigned,
    embedded,
    orientation,
    onOpenWeldParameters,
}) => {
    const dispatch = useDispatch();
    
    // Use parsed location data if available, otherwise it will be null
    const locationData = device.location?.parsed || null;
    const deviceErrors = getDeviceErrors(device);

    // State for download functionality
    const [showWeldParamConfirmDialog, setShowWeldParamConfirmDialog] = useState(false);
    const [showOpenWeldParametersDialog, setShowOpenWeldParametersDialog] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadingParams, setDownloadingParams] = useState(false);
    const [currentDownloadId, setCurrentDownloadId] = useState<string | null>(null);

    // Download progress context
    const { addDownload, updateProgress, completeDownload, failDownload, activeDownloads } = useDownloadProgress();

    // Initialize the weld parameter download service context when component mounts
    useEffect(() => {
        weldParameterDownloadService.setContext({ addDownload, updateProgress, completeDownload, failDownload, activeDownloads });
    }, [addDownload, updateProgress, completeDownload, failDownload, activeDownloads]);

    // Cleanup mock progress service on unmount
    useEffect(() => {
        return () => {
            if (currentDownloadId) {
                // Cleanup if needed
            }
        };
    }, [currentDownloadId]);

    // Listen for polling progress updates
    useEffect(() => {
        const handlePollingProgress = (event: any) => {
            if (event.detail && event.detail.deviceId === device.id) {
                // Handle weld parameter download progress updates
                if (event.detail.data) {
                    console.log('Weld parameter polling update received:', event.detail.data.weld_parameter_logs);
                    weldParameterDownloadService.handlePollingUpdate(
                        device.id,
                        device.name || `Device ${device.id}`,
                        event.detail.data
                    );
                }
            }
        };

        // Listen for custom polling progress events
        window.addEventListener('pollingProgress', handlePollingProgress);

        return () => {
            window.removeEventListener('pollingProgress', handlePollingProgress);
        };
    }, [device.id, device.name]);

    // Redux state selectors
    const deviceState = useSelector((state: any) => state.deviceData || {});
    const {
        weldParams: weldParamsData = {},
        weldParamsLoading = false,
        weldParamsError = null
    } = deviceState;

    // Get device-specific data
    const deviceWeldParams = weldParamsData[device.id];

    // Get alerts from device data only
    const alerts = getDeviceAlerts(device);

    // Determine connection status from device data
    const getConnectionStatus = (): 'online' | 'offline' => {
        const connectionStatus = device.communication_status || device.deviceData?.communication_status;

        // Map connection status to online/offline
        if (connectionStatus === 'Online') {
            return 'online';
        } else if (connectionStatus === 'Error' || connectionStatus === 'Offline') {
            return 'offline';
        } else if (connectionStatus === 'In Progress' || connectionStatus === '' || !connectionStatus) {
            return 'offline';
        }

        return 'offline'; // Default to offline for any other status
    };

    const connectionStatus = getConnectionStatus();
    const isOffline = connectionStatus === 'offline';
    const isOnline = connectionStatus === 'online';

    // Create device title with station and bug type information
    const getDeviceTitle = () => {
        const weldTypeName = WeldTypeNames[device.metadata.weldType as keyof typeof WeldTypeNames] || device.metadata.weldType || 'Unknown Device';

        // if (locationData) {
        //   const station = locationData.Station ? `Station ${locationData.Station}` : '';
        //   const bugType = locationData.BugType ? ` - ${locationData.BugType}` : '';
        //   return `${weldTypeName} (${station}${bugType})`;
        // }

        return weldTypeName;
    };
    const getDeviceConnectionStatus = (device: Device) => {
        const connectionStatus = device.communication_status || device.deviceData?.communication_status;

        // Return the actual connection status from device data
        if (connectionStatus) {
            return connectionStatus;
        }

        // Default to Offline if no connection status
        return 'Offline';
    };

    // Get status-based background color
    const getStatusBackgroundColor = () => {
        const statusConfig = Object.values(userStatusMap).find(config =>
            config.backends.includes(device.status)
        );

        if (statusConfig) {
            switch (statusConfig.label) {
                case 'Active':
                    return 'bg-green-50';
                case 'Registered':
                    return 'bg-emerald-50';
                case 'Registration Pending':
                    return 'bg-amber-50';
                case 'Admin Approval':
                    return 'bg-blue-50';
                case 'Decommissioned':
                    return 'bg-red-50';
                default:
                    return 'bg-gray-50 border-gray-200';
            }
        }

        return 'bg-white border-gray-200';
    };

    // Get status icon component
    const getStatusIconComponent = () => {
        const statusConfig = Object.values(userStatusMap).find(config =>
            config.backends.includes(device.status)
        );

        if (statusConfig) {
            const IconComponent = statusConfig.icon;
            return <IconComponent className={`h-5 w-5 ${statusConfig.color}`} />;
        }

        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    };


    // Helper function to get location data from JSON string
    const getLocationData = (field: 'Station' | 'BugType' | 'BugNum') => {
        try {
            if (device.location?.data &&
                device.location.data !== 'In Progress' &&
                device.location.data !== '' &&
                device.location.data !== 'Timeout') {
                const parsed = JSON.parse(device.location.data);
                return parsed[field] || '-';
            }
        } catch (error) {
            console.error('Error parsing location data:', error);
        }
        return '-';
    };

    // Get weld parameters from device data
    const getWeldParameters = (device: Device) => {
        if (device.weld_parameter_logs?.data && device.weld_parameter_logs.data === 'Weld Parameter has been downloaded successfully') {
            return 'Downloaded';
        } else {
            return 'Not Downloaded';
        }
    };

    // Handle download parameters function
    const handleDownloadParameters = (device: Device) => {
        // Check if weld parameters are already downloaded
        const weldParamsData = device.weld_parameter_logs?.data;
        if (weldParamsData === 'Weld Parameter has been downloaded successfully') {
            // Show confirmation dialog for re-download
            setShowWeldParamConfirmDialog(true);
            return;
        }

        // Start the download process
        startWeldParameterDownload();
    };

    const startWeldParameterDownload = () => {
        setDownloadingParams(true);
        
        // Start progress tracking
        const downloadId = weldParameterDownloadService.startDownload(
            device.id, 
            device.name || `Device ${device.id}`
        );
        setCurrentDownloadId(downloadId);

        // Dispatch the actual API call
        dispatch(weldParams({ 
            deviceId: device.id, 
            command: 'WeldParams', 
            weldType: device.metadata.weldType, 
            weldUID: device.metadata.weldUID 
        }));

        // Show download progress modal
        setShowDownloadModal(true);
    };

    const handleConfirmWeldParamDownload = () => {
        setShowWeldParamConfirmDialog(false);
        startWeldParameterDownload();
    };

    const handleOpenWeldParameters = () => {
        setShowOpenWeldParametersDialog(false);
        onOpenWeldParameters?.();
    };

    // Show download modal when weld params are successfully retrieved
    useEffect(() => {
        // console.log('deviceWeldParams', deviceWeldParams);
        // console.log('downloadingParams', downloadingParams);

        // Handle weld parameter download progress updates
        if (deviceWeldParams?.data && downloadingParams && currentDownloadId) {
            // Handle both string and JSON response formats
            let weldParamData = deviceWeldParams.data;
            if (typeof weldParamData === 'object' && weldParamData.data) {
                weldParamData = weldParamData.data;
                console.log('DeviceRow - Extracted message from JSON:', weldParamData);
            }
            
            if (typeof weldParamData === 'string') {
                // Check if download is completed successfully
                if (weldParamData.includes('Weld Parameter has been downloaded successfully')) {
                    console.log('Weld parameter download completed successfully');
                    setDownloadingParams(false);
                    if (onOpenWeldParameters) {
                        setShowDownloadModal(false);
                        setShowOpenWeldParametersDialog(true);
                    } else {
                        setShowDownloadModal(true);
                    }
                    // Complete the download in progress tracking
                    completeDownload(currentDownloadId);
                    setCurrentDownloadId(null);
                }
                // Check if download failed (same message for 60 seconds)
                else if (weldParamData.includes('Weld parameter download failed - same message for 60 seconds')) {
                    console.log('Weld parameter download failed - same message for 60 seconds');
                    setDownloadingParams(false);
                    // Fail the download in progress tracking
                    failDownload(currentDownloadId, 'Download failed - same message for 60 seconds');
                    setCurrentDownloadId(null);
                }
                // Check if download is initiated
                else if (weldParamData.includes('Weld Parameter Download is initiated, please check back later')) {
                    console.log('Weld parameter download initiated');
                    // Update progress to show initiated status
                    updateProgress(currentDownloadId, {
                        status: 'in_progress',
                        currentFile: weldParamData,
                        progress: 5
                    });
                }
                // Check for chunk progress updates (format: "5 out of 40 chunks processed")
                else if (weldParamData.includes('out of') && weldParamData.includes('chunks processed')) {
                    console.log('Weld parameter download progress:', weldParamData);
                    // Parse chunk progress - handles both "Chunks - 5 out of 40 chunks processed" and "5 out of 40 chunks processed"
                    const chunkMatch = weldParamData.match(/(\d+)\s+out\s+of\s+(\d+)\s+chunks\s+processed/);
                    if (chunkMatch) {
                        const currentChunk = parseInt(chunkMatch[1]);
                        const totalChunks = parseInt(chunkMatch[2]);
                        const progress = Math.round((currentChunk / totalChunks) * 100);
                        
                        console.log(`Progress: ${currentChunk}/${totalChunks} chunks (${progress}%)`);
                        
                        // If all chunks are processed, mark as completed
                        if (currentChunk >= totalChunks) {
                            console.log('All chunks processed, marking download as completed');
                            completeDownload(currentDownloadId);
                            setDownloadingParams(false);
                            if (onOpenWeldParameters) {
                                setShowDownloadModal(false);
                                setShowOpenWeldParametersDialog(true);
                            }
                            setCurrentDownloadId(null);
                        } else {
                            updateProgress(currentDownloadId, {
                                status: 'in_progress',
                                currentFile: weldParamData,
                                progress: progress,
                                totalFiles: totalChunks,
                                downloadedFiles: currentChunk
                            });
                        }
                    } else {
                        // Update with raw message
                        updateProgress(currentDownloadId, {
                            status: 'in_progress',
                            currentFile: weldParamData
                        });
                    }
                }
                // Handle other progress messages
                else if (weldParamData.includes('In Progress') || weldParamData.includes('Processing')) {
                    console.log('Weld parameter download in progress:', weldParamData);
                    updateProgress(currentDownloadId, {
                        status: 'in_progress',
                        currentFile: weldParamData
                    });
                }
            }
        }

        // Handle errors
        if (deviceWeldParams?.error && downloadingParams && currentDownloadId) {
            console.log('Weld params error:', deviceWeldParams.error);
            failDownload(currentDownloadId, deviceWeldParams.error);
            setCurrentDownloadId(null);
        }
    }, [deviceWeldParams, downloadingParams, currentDownloadId, completeDownload, failDownload, updateProgress, onOpenWeldParameters]);

    // Build flat list of fields to show depending on online/offline state.
    const fields: { label: string; value: React.ReactNode }[] = isOnline
        ? [
              { label: 'Station', value: locationData?.Station || '-' },
              { label: 'Bug Type', value: locationData?.BugType || '-' },
              {
                  label: 'Network',
                  value: (
                      <span className={isOnline ? 'text-galvanizing-green' : 'text-readable-red'}>
                          {getDeviceConnectionStatus(device)}
                      </span>
                  ),
              },
              { label: 'Weld Parameters', value: isOffline ? '-' : getWeldParameters(device) },
              {
                  label: 'Logs',
                  value: isOffline
                      ? '-'
                      : (() => {
                          const raw = device.number_of_logs?.data;
                          if (raw == null) return '0';
                          if (typeof raw === 'number') return String(raw);
                          if (typeof raw !== 'string') return '0';
                          try {
                              const parsed = JSON.parse(raw);
                              if (parsed != null && typeof parsed === 'object' && 'NumberOfLogs' in parsed) {
                                  return String(parsed.NumberOfLogs ?? '0');
                              }
                              if (typeof parsed === 'number') return String(parsed);
                          } catch {
                              return raw;
                          }
                          return raw;
                      })(),
              },
              { label: 'Weld Machine Connected', value: device.weldMachineConnected ? 'Yes' : 'No' },
              { label: 'Firmware Version', value: device.EdgeFWVersion || '-' },
              {
                  label: 'Last Seen',
                  value: isOffline
                      ? '-'
                      : device.lastSeen
                      ? formatDate(device.lastSeen, true)
                      : formatDate(new Date().toISOString(), true),
              },
          ]
        : [
              {
                  label: 'Network',
                  value: (
                      <span className="text-readable-red">{getDeviceConnectionStatus(device)}</span>
                  ),
              },
              { label: 'Weld Machine Connected', value: device.weldMachineConnected ? 'Yes' : 'No' },
              {
                  label: 'Last Seen',
                  value: device.lastSeen ? formatDate(device.lastSeen, true, true) : '-',
              },
          ];

    const openWeldParametersDialog = (
        <Dialog open={showOpenWeldParametersDialog} onOpenChange={setShowOpenWeldParametersDialog}>
            <DialogContent className="max-w-[630px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-blueprint-blue">
                        <CheckCircle className="h-5 w-5 text-galvanizing-green" />
                        Weld Parameters Downloaded
                    </DialogTitle>
                    <DialogDescription className="text-[13px] text-text-grey">
                        Weld parameters have been downloaded successfully for <strong>{`${device.id} - ${getDeviceTitle()}`}</strong>.
                        Do you want to open Weld Parameters now?
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setShowOpenWeldParametersDialog(false)}
                    >
                        No
                    </Button>
                    <Button
                        onClick={handleOpenWeldParameters}
                        className="bg-hero-red hover:bg-hero-red/90 text-white"
                    >
                        Yes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    // Compact embedded view — rendered inside a StationCard, no wrapper Card.
    if (embedded) {
        return (
            <>
                <div className="p-4 min-w-0">
                    {/* Orientation label + download button */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                            {orientation}
                        </span>
                        <Button
                            size="sm"
                            className="flex items-center gap-1.5 h-7 px-2.5 bg-hero-red hover:bg-hero-red/90 text-white text-[11px] font-medium flex-shrink-0"
                            onClick={() => handleDownloadParameters(device)}
                        >
                            <Download className="h-3 w-3" />
                            Weld Params
                        </Button>
                    </div>

                    {/* Device title + id + status */}
                    <div className="flex items-center gap-2 mb-3 min-w-0">
                        <div className="flex-shrink-0 relative">
                            <div className="w-9 h-9 bg-gradient-to-br from-blueprint-blue to-[#003a5f] rounded-md flex items-center justify-center">
                                <Server className="h-4 w-4 text-white" />
                            </div>
                            <div className="absolute -right-0.5 -top-0.5 scale-75">
                                {getStatusIcon(
                                    device.communication_status || device.deviceData?.communication_status || '',
                                    deviceErrors.length > 0,
                                    isOnline
                                )}
                            </div>
                        </div>
                        <div className="min-w-0">
                            <h4 className="text-[13px] font-semibold text-blueprint-blue leading-tight truncate">
                                {getDeviceTitle()}
                            </h4>
                            <p className="text-[10px] text-heritage-grey font-mono truncate">{device.id}</p>
                        </div>
                    </div>

                    {/* Key fields — compact 2-col */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                        {fields.map(({ label, value }) => (
                            <div key={label} className="min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey mb-0.5">
                                    {label}
                                </div>
                                <div className="text-[12px] font-medium text-text-grey truncate">{value}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <WeldParameterDownloadDialog
                    isOpen={showWeldParamConfirmDialog}
                    onClose={() => setShowWeldParamConfirmDialog(false)}
                    onConfirm={handleConfirmWeldParamDownload}
                    deviceName={device.name || `Device ${device.id}`}
                    isLoading={weldParamsLoading}
                />
                <DownloadProgressModal
                    isOpen={showDownloadModal}
                    onClose={() => setShowDownloadModal(false)}
                />
                {openWeldParametersDialog}
            </>
        );
    }

    return (
        <>
            <Card className={`border border-[#E2E5EA] shadow-sm ${unassigned ? 'bg-[rgba(230,161,0,0.04)]' : ''}`}>
                <CardContent className="p-4">
                    {/* Meta row: station label + device id (single source of truth for id) */}
                    {stationLabel && (
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#E2E5EA]">
                            <span className={`text-[11px] font-medium ${unassigned ? 'text-[#B38600]' : 'text-heritage-grey'}`}>
                                {unassigned && '⚠ '}
                                {stationLabel}
                            </span>
                            <span className="text-[11px] text-heritage-grey font-mono truncate ml-2">{device.id}</span>
                        </div>
                    )}

                    {/* Device header: icon + title + action */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <div className="flex-shrink-0 relative">
                                <div className="w-10 h-10 bg-gradient-to-br from-blueprint-blue to-[#003a5f] rounded-lg flex items-center justify-center">
                                    <Server className="h-5 w-5 text-white" />
                                </div>
                                <div className="absolute -right-1 -top-1 scale-75">
                                    {getStatusIcon(
                                        device.communication_status || device.deviceData?.communication_status || '',
                                        deviceErrors.length > 0,
                                        isOnline
                                    )}
                                </div>
                            </div>
                            <h3 className="text-[14px] font-semibold text-blueprint-blue leading-tight truncate">
                                {getDeviceTitle()}
                            </h3>
                        </div>

                        <Button
                            size="sm"
                            className="flex items-center gap-1.5 h-8 px-3 bg-hero-red hover:bg-hero-red/90 text-white text-[12px] font-medium flex-shrink-0"
                            onClick={() => handleDownloadParameters(device)}
                        >
                            <Download className="h-3.5 w-3.5" />
                            Download Weld Parameters
                        </Button>
                    </div>

                    {/* Flat field grid — compact 2-col layout */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                        {fields.map(({ label, value }) => (
                            <div key={label} className="min-w-0">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey mb-0.5">
                                    {label}
                                </div>
                                <div className="text-[12px] font-medium text-text-grey truncate">{value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Alerts (offline devices only) */}
                    {!isOnline && alerts.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-[#E2E5EA] flex flex-wrap gap-2">
                            {alerts.map((alert, index) => (
                                <div
                                    key={index}
                                    className={`flex items-start gap-2 rounded-lg px-3 py-2 max-w-[320px] ${
                                        alert.type === 'error'
                                            ? 'bg-[rgba(196,0,31,0.06)]'
                                            : 'bg-[rgba(230,161,0,0.08)]'
                                    }`}
                                >
                                    {alert.type === 'error' ? (
                                        <XCircle className="h-4 w-4 text-readable-red mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle className="h-4 w-4 text-[#B38600] mt-0.5 flex-shrink-0" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={`text-[12px] font-medium ${
                                                alert.type === 'error' ? 'text-readable-red' : 'text-[#B38600]'
                                            }`}
                                        >
                                            {alert.type === 'error' ? 'Error' : 'Warning'}: {alert.code}
                                        </div>
                                        <div
                                            className={`text-[11px] mt-0.5 ${
                                                alert.type === 'error' ? 'text-readable-red/80' : 'text-[#B38600]/80'
                                            }`}
                                        >
                                            {alert.message}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Weld Parameter Download Confirmation Dialog */}
            <WeldParameterDownloadDialog
                isOpen={showWeldParamConfirmDialog}
                onClose={() => setShowWeldParamConfirmDialog(false)}
                onConfirm={handleConfirmWeldParamDownload}
                deviceName={device.name || `Device ${device.id}`}
                isLoading={weldParamsLoading}
            />

            {/* Download Progress Modal */}
            <DownloadProgressModal
                isOpen={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
            />
            {openWeldParametersDialog}
        </>
    );
};
